import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCart } from "../../context/CartContext";
import { X } from "lucide-react";
import { DISCOUNT_TYPE_CATEGORY } from "../../utils/constants";
import { useCouponContext } from "../../context/CouponContext";
import { useUserAuth } from "../../context/UserAuthContext";
import { validateCouponCode } from "../../services/couponService";
import DiscountIcon from "@mui/icons-material/Discount";

export const CHECKOUT_APPLIED_COUPON_CODE_KEY = "checkout_applied_coupon_code";

const safeStorage = {
  set(key, value) {
    try {
      localStorage.setItem(key, value);
      return;
    } catch {
      // ignore
    }
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // ignore
    }
  },
  get(key) {
    try {
      const v = localStorage.getItem(key);
      if (v != null) return v;
    } catch {
      // ignore
    }
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

export function getStoredCheckoutCouponCode() {
  return String(safeStorage.get(CHECKOUT_APPLIED_COUPON_CODE_KEY) || "").trim();
}

const CouponPage = ({
  subtotal,
  shippingFee = 0,
  gstTotal = 0,
  maxCouponDiscount,
  setCouponDiscount,
}) => {
  const { cart } = useCart();
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  /** Stable row identity so “Applied” never snaps to another row with the same code. */
  const [appliedRowKey, setAppliedRowKey] = useState(null);
  const [customCode, setCustomCode] = useState("");
  const [error, setError] = useState("");
  const [showAll, setShowAll] = useState(false);

  const { coupons, loadCoupons, refreshCoupons, loading } = useCouponContext();
  const { user } = useUserAuth();
  const userId = user?._id;

  const visibleCoupons = useMemo(
    () => (coupons || []).filter((c) => c.showInUI !== false),
    [coupons],
  );

  const vendorKey = useMemo(
    () =>
      cart?.length
        ? cart
            .map((i) => String(i.product?.vendorId ?? ""))
            .sort()
            .join("|")
        : "",
    [cart],
  );

  const cartRef = useRef(cart);
  cartRef.current = cart;

  useEffect(() => {
    loadCoupons(cartRef.current);
  }, [vendorKey, loadCoupons]);

  const evaluateCondition = useCallback((condition, cartItems) => {
    if (!condition || !condition.type) return true;

    if (
      condition.type === DISCOUNT_TYPE_CATEGORY.excludeProductByNameIncludes
    ) {
      return !cartItems.some((item) =>
        item.product.name
          .toLowerCase()
          .includes((condition.value || "").toLowerCase()),
      );
    }

    if (condition.type === DISCOUNT_TYPE_CATEGORY.includeProductType) {
      const couponTags = Array.isArray(condition.value)
        ? condition.value.map((t) => t.toLowerCase())
        : [String(condition.value).toLowerCase()];

      return cartItems.some((item) => {
        const productTags = Array.isArray(item.product.type)
          ? item.product.type.map((t) => t.toLowerCase())
          : [];

        return productTags.some((tag) => couponTags.includes(tag));
      });
    }

    if (condition.type === DISCOUNT_TYPE_CATEGORY.excludeProductType) {
      const couponTags = Array.isArray(condition.value)
        ? condition.value.map((t) => t.toLowerCase())
        : [String(condition.value).toLowerCase()];

      return !cartItems.some((item) => {
        const productTags = Array.isArray(item.product.type)
          ? item.product.type.map((t) => t.toLowerCase())
          : [];

        return productTags.some((tag) => couponTags.includes(tag));
      });
    }

    return true;
  }, []);

  const getCouponRowKey = useCallback((c) => {
    if (!c) return "";
    const id = c._id != null && c._id !== "" ? String(c._id) : "";
    if (id) return `id:${id}`;
    const vid = c.vendorId != null ? String(c.vendorId) : "";
    if (vid && c.code) return `v:${vid}:${String(c.code).toLowerCase()}`;
    return `c:${String(c.code || "").toLowerCase()}`;
  }, []);

  useEffect(() => {
    if (!selectedCoupon) return;
    if (!evaluateCondition(selectedCoupon.condition, cart)) {
      setSelectedCoupon(null);
      setAppliedRowKey(null);
      setCouponDiscount(0);
      setError("Coupon removed due to cart change.");
    }
  }, [cart, selectedCoupon, evaluateCondition, setCouponDiscount]);

  /** Only clear selection if this coupon is gone — do not replace with coupons.find (first code match). */
  useEffect(() => {
    if (!selectedCoupon?.code || !appliedRowKey) return;
    if (!Array.isArray(coupons) || coupons.length === 0) {
      setSelectedCoupon(null);
      setAppliedRowKey(null);
      setCouponDiscount(0);
      return;
    }
    const stillThere = coupons.some(
      (c) => getCouponRowKey(c) === appliedRowKey,
    );
    if (!stillThere) {
      setSelectedCoupon(null);
      setAppliedRowKey(null);
      setCouponDiscount(0);
      setError("Coupon is no longer available for this cart.");
    }
  }, [coupons, selectedCoupon, appliedRowKey, getCouponRowKey, setCouponDiscount]);

  const findCoupon = (list, codeNorm) =>
    list.find((c) => c.code.toLowerCase() === codeNorm.toLowerCase());

  /** Prefer the row’s coupon object so the correct row applies (avoids wrong match after refresh). */
  const handleApply = async (codeOrCoupon) => {
    const fromRow =
      codeOrCoupon && typeof codeOrCoupon === "object" ? codeOrCoupon : null;
    const normalized = String(
      fromRow?.code ?? codeOrCoupon ?? "",
    ).trim();
    if (!normalized) {
      setError("Enter a coupon code.");
      return;
    }

    let list = coupons;
    const pickFromList = (arr) => {
      if (!arr?.length) return null;
      if (fromRow?._id != null && fromRow._id !== "") {
        const byId = arr.find(
          (c) => String(c?._id ?? "") === String(fromRow._id),
        );
        if (byId) return byId;
      }
      const sameCode = arr.filter(
        (c) => c.code.toLowerCase() === normalized.toLowerCase(),
      );
      if (sameCode.length === 1) return sameCode[0];
      if (sameCode.length > 1 && fromRow) {
        const byRef = sameCode.find((c) => c === fromRow);
        if (byRef) return byRef;
        if (fromRow._id != null && fromRow._id !== "") {
          const byId2 = sameCode.find(
            (c) => String(c?._id ?? "") === String(fromRow._id),
          );
          if (byId2) return byId2;
        }
        const vid = fromRow.vendorId != null ? String(fromRow.vendorId) : "";
        if (vid) {
          const byVendor = sameCode.find(
            (c) => String(c?.vendorId ?? "") === vid,
          );
          if (byVendor) return byVendor;
        }
      }
      return findCoupon(arr, normalized);
    };

    let coupon = pickFromList(list);

    if (!coupon) {
      list = await refreshCoupons(cart);
      coupon = pickFromList(list || []);
    }

    if (!coupon) {
      try {
        const validation = await validateCouponCode(normalized, cart, userId);
        if (!validation?.valid) {
          setError(validation?.message || "Invalid coupon code.");
          return;
        }
        coupon = validation.coupon;
      } catch {
        setError("Could not validate coupon. Please try again.");
        return;
      }
    }

    if (!coupon) {
      setError("Invalid coupon code.");
      return;
    }

    const cartSubtotal = subtotal;

    const passesCondition = evaluateCondition(coupon.condition, cart);
    const meetsMinCart =
      !coupon.minCartValue || cartSubtotal >= coupon.minCartValue;

    if (passesCondition && meetsMinCart) {
      let discount = 0;
      if (coupon.type === "amount") {
        discount = coupon.value;
      } else if (coupon.type === "percentage") {
        discount = (cartSubtotal * coupon.value) / 100;
        if (coupon.maxDiscountValue && discount > coupon.maxDiscountValue) {
          discount = coupon.maxDiscountValue;
        }
      }

      const payableBefore = Number(cartSubtotal) + Number(shippingFee) + Number(gstTotal);
      const maxFromParent =
        typeof maxCouponDiscount === "number" && !Number.isNaN(maxCouponDiscount)
          ? maxCouponDiscount
          : Math.max(0, payableBefore - 1);
      const maxAllowed = Math.max(0, maxFromParent);

      if (maxAllowed <= 0) {
        setSelectedCoupon(null);
        setAppliedRowKey(null);
        setCouponDiscount(0);
        setError("Order total is too low to apply this coupon.");
        return;
      }

      const capped = Math.min(Number(discount) || 0, maxAllowed);

      setSelectedCoupon(coupon);
      setAppliedRowKey(getCouponRowKey(coupon));
      setCouponDiscount(Number.isFinite(capped) ? capped : 0);
      setError("");
      safeStorage.set(
        CHECKOUT_APPLIED_COUPON_CODE_KEY,
        String(coupon.code || ""),
      );
    } else {
      setSelectedCoupon(null);
      setAppliedRowKey(null);
      setCouponDiscount(0);

      if (!passesCondition) {
        if (coupon.flashRestriction) {
          setError(
            "This coupon cannot be used with flash sale items. Please remove restricted items or choose another coupon.",
          );
        } else {
          setError("This coupon is not applicable to your cart.");
        }
      } else if (!meetsMinCart) {
        setError(`Minimum cart value of ₹${coupon.minCartValue} is required.`);
      }
    }
  };

  const removeCoupon = () => {
    setSelectedCoupon(null);
    setAppliedRowKey(null);
    setCouponDiscount(0);
    setError("");
    safeStorage.remove(CHECKOUT_APPLIED_COUPON_CODE_KEY);
  };

  // Re-apply coupon after login redirect / refresh (session-scoped).
  useEffect(() => {
    if (selectedCoupon || appliedRowKey) return;
    if (!Array.isArray(coupons) || coupons.length === 0) return;
    let saved = "";
    saved = String(safeStorage.get(CHECKOUT_APPLIED_COUPON_CODE_KEY) || "").trim();
    if (!saved) return;
    handleApply(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupons, selectedCoupon, appliedRowKey]);

  return (
    <div className="mt-4 rounded-2xl border border-dashed border-orange-200/90 bg-orange-50/30 p-4 sm:p-5">
      <h3 className="font-semibold mb-2">
        <DiscountIcon
          className="mr-1"
          sx={{
            color: "crimson",
          }}
        />{" "}
        Available Coupons ({visibleCoupons.length})
      </h3>
      {coupons.length > visibleCoupons.length && (
        <p className="text-xs text-gray-500 mb-2">
          Additional codes may apply—enter them in the box below.
        </p>
      )}

      {selectedCoupon && (
        <div className="flex justify-between items-center bg-green-50 text-green-700 px-4 py-2 rounded mb-3">
          <div>
            <p className="font-semibold">{selectedCoupon.code}</p>
            <p className="text-sm">{selectedCoupon.label}</p>
          </div>
          <button
            type="button"
            onClick={removeCoupon}
            className="text-red-600 text-xl font-bold"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {error && (
        <p className="text-sm mb-3 bg-red-100 text-red-600 p-2 rounded">
          {error}
        </p>
      )}

      {visibleCoupons.length > 2 && (
        <button
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          className="mb-3 w-full px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
        >
          {showAll
            ? "Show fewer coupons"
            : `View all ${visibleCoupons.length} coupons`}
        </button>
      )}

      <div className="space-y-2">
        {(showAll ? visibleCoupons : visibleCoupons.slice(0, 2)).map(
          (coupon) => {
          const rowKey = coupon._id ? String(coupon._id) : coupon.code;
          const isApplied =
            !!appliedRowKey && getCouponRowKey(coupon) === appliedRowKey;
          return (
            <div
              key={rowKey}
              className="flex justify-between items-center bg-gray-50 p-3 rounded border"
            >
              <div>
                <p className="text-sm font-medium">{coupon.label}</p>
                <p className="text-xs text-gray-500">{coupon.code}</p>
              </div>
              <button
                type="button"
                onClick={() => handleApply(coupon)}
                disabled={isApplied || loading}
                className={`px-3 py-1 text-sm rounded ${
                  isApplied
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-black hover:bg-gray-800"
                } text-white`}
              >
                {isApplied ? "Applied" : "Apply"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <label className="text-sm text-gray-600">
          Or enter your coupon code (includes unlisted codes):
        </label>
        <div className="flex mt-1 gap-2">
          <input
            className="border p-2 flex-grow rounded text-sm"
            value={customCode}
            onChange={(e) => setCustomCode(e.target.value)}
          />
          <button
            type="button"
            onClick={() => handleApply(String(customCode).trim())}
            disabled={loading}
            className="bg-black text-white px-4 py-2 rounded text-sm"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default CouponPage;
